/**
 * Particles - particulas em GPU, com perfil por superficie.
 *
 * Ideia central: a CPU so ESCREVE o estado inicial de cada particula uma unica vez
 * (posicao, velocidade, vida, cor). Depois disso ninguem mais toca nela: o vertex
 * shader integra a trajetoria em forma FECHADA a partir do tempo decorrido. Isso
 * significa custo zero de CPU por frame, mesmo com milhares de particulas vivas,
 * e nenhuma alocacao dentro do laco de frame.
 *
 * Dois pools, um por modo de mistura (2 draw calls no total):
 *   pool ALPHA    -> poeira, fumaca, lascas, nevoa (mistura normal, recebe fog)
 *   pool ADITIVO  -> faiscas, estilhacos brilhantes, brasa, fogo
 *
 * Iluminacao: a poeira NAO e chapada. Ela recebe (a) dispersao frontal - fica bem
 * mais clara quando o sol esta atras dela em relacao a camera, que e o que da a
 * leitura de "ar com particulado no fim de tarde" - e (b) um sombreado volumetrico
 * falso, usando uma normal esferica derivada do proprio quad. Sem isso a poeira
 * vira um borrao cinza colado na tela, que e a marca de demo tecnico.
 *
 * No fim do arquivo mora tambem AmbientDust: o ar com particulado PERMANENTE do
 * mapa (nada a ver com tiro), que reaproveita este mesmo atlas e a direcao/cor
 * de sol lidas aqui. Ele e construido e atualizado pelo FxSystem.
 */

import * as THREE from 'three';
import { LAYERS } from '../core/Constants.js';

/** Tipos de particula. O tipo escolhe o tile do atlas e o pool. */
export const PKIND = {
  DUST: 0,   // poeira fina, sobe e dispersa
  SMOKE: 1,  // fumaca, maior e mais lenta
  CHIP: 2,   // lasca/detrito solido, quica no chao
  SPARK: 3,  // faisca esticada pela velocidade (rastro)
  SHARD: 4,  // estilhaco de vidro, cintila
  MIST: 5,   // nevoa curta (sangue)
  EMBER: 6,  // brasa arredondada, cai devagar
  FIRE: 7    // bola de fogo, expande e apaga
};

const ADDITIVE_KINDS = [false, false, false, true, true, false, true, true];

const TAU = Math.PI * 2;

/** Capacidades fixas: dimensionadas para o preset ultra sem nunca crescer. */
const CAP_ALPHA = 1600;
const CAP_ADD = 900;

/**
 * Descritor de camada. Cada perfil de superficie e uma lista de camadas
 * (ex.: concreto = poeira clara + nuvem lenta + lascas escuras).
 * Objetos criados uma unica vez no carregamento do modulo - nunca por tiro.
 */
function L(o) {
  return {
    kind: o.kind ?? PKIND.DUST,
    count: o.count ?? 6,
    life0: o.life0 ?? 0.6, life1: o.life1 ?? 1.1,
    size0: o.size0 ?? 0.04, size1: o.size1 ?? 0.3,
    sizeJit: o.sizeJit ?? 0.35,
    spd0: o.spd0 ?? 1.0, spd1: o.spd1 ?? 3.0,
    cone: o.cone ?? 0.9,       // meio-angulo do cone em torno da normal (rad)
    up: o.up ?? 0.0,           // empurrao extra em +Y (m/s)
    jit: o.jit ?? 0.02,        // dispersao do ponto de nascimento (m)
    off: o.off ?? 0.025,       // deslocamento ao longo da normal (m)
    grav: o.grav ?? 0.0,       // negativo = flutua (poeira quente sobe)
    drag: o.drag ?? 1.8,       // arrasto linear (usado quando rest == 0)
    rest: o.rest ?? 0.0,       // > 0 liga o modelo balistico com quique
    cr: o.cr ?? 0.72, cg: o.cg ?? 0.70, cb: o.cb ?? 0.66,
    colJit: o.colJit ?? 0.12,
    alpha: o.alpha ?? 0.45,
    spin: o.spin ?? 2.0,
    turb: o.turb ?? 0.0,       // deriva lateral (m em 1 s)
    stretch: o.stretch ?? 0.0, // > 0 estica o quad na direcao da velocidade
    fadeIn: o.fadeIn ?? 0.08,
    hold: o.hold ?? 0.2        // fracao da vida antes de comecar a sumir
  };
}

// ---------------------------------------------------------------------------
// Perfis por superficie. Nomes vindos de SURFACES (core/Constants.js).
// ---------------------------------------------------------------------------

const DUST_STONE = L({
  kind: PKIND.DUST, count: 13, life0: 0.9, life1: 1.8,
  size0: 0.05, size1: 0.58, spd0: 0.8, spd1: 2.4, cone: 0.95,
  grav: -0.32, drag: 1.9, cr: 0.76, cg: 0.74, cb: 0.70,
  alpha: 0.40, turb: 0.11, spin: 1.5, hold: 0.16
});
const CLOUD_STONE = L({
  kind: PKIND.DUST, count: 4, life0: 1.4, life1: 2.6,
  size0: 0.10, size1: 1.05, spd0: 0.2, spd1: 0.8, cone: 1.3,
  grav: -0.45, drag: 1.3, cr: 0.72, cg: 0.70, cb: 0.66,
  alpha: 0.20, turb: 0.18, spin: 0.7, hold: 0.1
});
const CHIPS_STONE = L({
  kind: PKIND.CHIP, count: 9, life0: 0.9, life1: 1.6,
  size0: 0.017, size1: 0.013, spd0: 2.8, spd1: 7.4, cone: 0.85,
  grav: 22, rest: 0.34, cr: 0.27, cg: 0.26, cb: 0.245,
  alpha: 1.0, spin: 17, fadeIn: 0.01, hold: 0.78
});

const PROFILES = {
  concrete: [DUST_STONE, CLOUD_STONE, CHIPS_STONE],

  asphalt: [
    L({ kind: PKIND.DUST, count: 10, life0: 0.8, life1: 1.5, size0: 0.05, size1: 0.5,
        spd0: 0.7, spd1: 2.1, cone: 0.95, grav: -0.25, drag: 2.0,
        cr: 0.40, cg: 0.39, cb: 0.37, alpha: 0.36, turb: 0.10, hold: 0.15 }),
    L({ kind: PKIND.CHIP, count: 8, life0: 0.8, life1: 1.4, size0: 0.016, size1: 0.012,
        spd0: 2.4, spd1: 6.2, cone: 0.8, grav: 22, rest: 0.24,
        cr: 0.13, cg: 0.13, cb: 0.125, alpha: 1.0, spin: 15, fadeIn: 0.01, hold: 0.78 })
  ],

  brick: [
    L({ kind: PKIND.DUST, count: 13, life0: 0.9, life1: 1.8, size0: 0.05, size1: 0.56,
        spd0: 0.8, spd1: 2.4, cone: 0.95, grav: -0.30, drag: 1.9,
        cr: 0.70, cg: 0.47, cb: 0.36, alpha: 0.42, turb: 0.11, spin: 1.5, hold: 0.16 }),
    L({ kind: PKIND.DUST, count: 4, life0: 1.4, life1: 2.5, size0: 0.10, size1: 1.0,
        spd0: 0.2, spd1: 0.8, cone: 1.3, grav: -0.42, drag: 1.3,
        cr: 0.64, cg: 0.44, cb: 0.34, alpha: 0.20, turb: 0.18, spin: 0.7, hold: 0.1 }),
    L({ kind: PKIND.CHIP, count: 9, life0: 0.9, life1: 1.6, size0: 0.018, size1: 0.014,
        spd0: 2.8, spd1: 7.0, cone: 0.85, grav: 22, rest: 0.30,
        cr: 0.36, cg: 0.19, cb: 0.13, alpha: 1.0, spin: 16, fadeIn: 0.01, hold: 0.78 })
  ],

  plaster: [
    L({ kind: PKIND.DUST, count: 16, life0: 1.0, life1: 2.0, size0: 0.05, size1: 0.66,
        spd0: 0.9, spd1: 2.6, cone: 1.0, grav: -0.40, drag: 1.8,
        cr: 0.86, cg: 0.84, cb: 0.80, alpha: 0.44, turb: 0.13, spin: 1.4, hold: 0.14 }),
    L({ kind: PKIND.DUST, count: 5, life0: 1.5, life1: 2.8, size0: 0.12, size1: 1.15,
        spd0: 0.2, spd1: 0.9, cone: 1.35, grav: -0.5, drag: 1.2,
        cr: 0.84, cg: 0.82, cb: 0.78, alpha: 0.20, turb: 0.2, spin: 0.6, hold: 0.1 }),
    L({ kind: PKIND.CHIP, count: 7, life0: 0.8, life1: 1.4, size0: 0.015, size1: 0.011,
        spd0: 2.2, spd1: 6.0, cone: 0.9, grav: 22, rest: 0.22,
        cr: 0.62, cg: 0.60, cb: 0.57, alpha: 1.0, spin: 14, fadeIn: 0.01, hold: 0.78 })
  ],

  tile: [
    L({ kind: PKIND.DUST, count: 9, life0: 0.7, life1: 1.4, size0: 0.04, size1: 0.42,
        spd0: 0.9, spd1: 2.4, cone: 0.9, grav: -0.25, drag: 2.1,
        cr: 0.80, cg: 0.79, cb: 0.77, alpha: 0.34, turb: 0.09, hold: 0.15 }),
    L({ kind: PKIND.CHIP, count: 11, life0: 0.9, life1: 1.6, size0: 0.019, size1: 0.014,
        spd0: 3.2, spd1: 8.0, cone: 0.9, grav: 22, rest: 0.42,
        cr: 0.55, cg: 0.54, cb: 0.53, alpha: 1.0, spin: 19, fadeIn: 0.01, hold: 0.78 }),
    L({ kind: PKIND.SPARK, count: 3, life0: 0.18, life1: 0.34, size0: 0.014, size1: 0.006,
        spd0: 3.0, spd1: 7.0, cone: 0.9, grav: 16, rest: 0.3,
        cr: 1.0, cg: 0.72, cb: 0.34, alpha: 1.6, stretch: 0.05, fadeIn: 0.005, hold: 0.2 })
  ],

  metal: [
    L({ kind: PKIND.SPARK, count: 17, life0: 0.34, life1: 0.8, size0: 0.021, size1: 0.007,
        spd0: 5.5, spd1: 14.0, cone: 1.0, grav: 17, rest: 0.46,
        cr: 1.0, cg: 0.58, cb: 0.17, colJit: 0.16, alpha: 2.4,
        stretch: 0.055, fadeIn: 0.004, hold: 0.22 }),
    L({ kind: PKIND.EMBER, count: 5, life0: 0.7, life1: 1.4, size0: 0.012, size1: 0.005,
        spd0: 1.6, spd1: 4.5, cone: 1.2, grav: 12, rest: 0.35,
        cr: 1.0, cg: 0.42, cb: 0.10, alpha: 1.2, fadeIn: 0.01, hold: 0.25 }),
    L({ kind: PKIND.SMOKE, count: 3, life0: 0.5, life1: 1.0, size0: 0.03, size1: 0.28,
        spd0: 0.5, spd1: 1.6, cone: 1.0, grav: -0.3, drag: 2.4,
        cr: 0.34, cg: 0.33, cb: 0.32, alpha: 0.26, turb: 0.09, hold: 0.12 }),
    L({ kind: PKIND.CHIP, count: 4, life0: 0.7, life1: 1.2, size0: 0.011, size1: 0.008,
        spd0: 3.0, spd1: 7.5, cone: 0.8, grav: 22, rest: 0.45,
        cr: 0.40, cg: 0.40, cb: 0.42, alpha: 1.0, spin: 20, fadeIn: 0.01, hold: 0.78 })
  ],

  metal_thin: [
    L({ kind: PKIND.SPARK, count: 12, life0: 0.28, life1: 0.62, size0: 0.019, size1: 0.006,
        spd0: 5.0, spd1: 12.0, cone: 1.05, grav: 17, rest: 0.44,
        cr: 1.0, cg: 0.62, cb: 0.22, colJit: 0.16, alpha: 2.1,
        stretch: 0.055, fadeIn: 0.004, hold: 0.22 }),
    L({ kind: PKIND.CHIP, count: 5, life0: 0.6, life1: 1.1, size0: 0.012, size1: 0.009,
        spd0: 3.4, spd1: 8.0, cone: 0.9, grav: 22, rest: 0.5,
        cr: 0.44, cg: 0.44, cb: 0.46, alpha: 1.0, spin: 22, fadeIn: 0.01, hold: 0.78 }),
    L({ kind: PKIND.SMOKE, count: 2, life0: 0.4, life1: 0.8, size0: 0.03, size1: 0.2,
        spd0: 0.4, spd1: 1.2, cone: 1.0, grav: -0.25, drag: 2.6,
        cr: 0.36, cg: 0.35, cb: 0.34, alpha: 0.2, turb: 0.08, hold: 0.12 })
  ],

  wood: [
    L({ kind: PKIND.CHIP, count: 11, life0: 0.9, life1: 1.7, size0: 0.024, size1: 0.018,
        spd0: 2.6, spd1: 7.0, cone: 0.85, grav: 21, rest: 0.26,
        cr: 0.30, cg: 0.20, cb: 0.11, alpha: 1.0, spin: 18, fadeIn: 0.01, hold: 0.76 }),
    L({ kind: PKIND.DUST, count: 11, life0: 0.9, life1: 1.7, size0: 0.04, size1: 0.4,
        spd0: 0.8, spd1: 2.4, cone: 1.0, grav: -0.18, drag: 2.0,
        cr: 0.64, cg: 0.49, cb: 0.30, alpha: 0.34, turb: 0.1, spin: 2.0, hold: 0.15 }),
    L({ kind: PKIND.SMOKE, count: 2, life0: 0.6, life1: 1.1, size0: 0.03, size1: 0.26,
        spd0: 0.3, spd1: 1.0, cone: 1.1, grav: -0.35, drag: 2.2,
        cr: 0.28, cg: 0.25, cb: 0.22, alpha: 0.18, turb: 0.12, hold: 0.1 })
  ],

  glass: [
    L({ kind: PKIND.SHARD, count: 14, life0: 0.55, life1: 1.15, size0: 0.026, size1: 0.014,
        spd0: 3.0, spd1: 9.5, cone: 1.05, grav: 20, rest: 0.18,
        cr: 0.82, cg: 0.93, cb: 1.0, alpha: 1.9, spin: 22, fadeIn: 0.004, hold: 0.3 }),
    L({ kind: PKIND.SHARD, count: 8, life0: 0.35, life1: 0.7, size0: 0.012, size1: 0.007,
        spd0: 2.0, spd1: 6.0, cone: 1.3, grav: 19, rest: 0.15,
        cr: 0.90, cg: 0.97, cb: 1.0, alpha: 1.3, fadeIn: 0.004, hold: 0.3 }),
    L({ kind: PKIND.DUST, count: 6, life0: 0.5, life1: 1.0, size0: 0.03, size1: 0.24,
        spd0: 0.8, spd1: 2.2, cone: 1.0, grav: -0.1, drag: 2.4,
        cr: 0.86, cg: 0.90, cb: 0.94, alpha: 0.22, turb: 0.08, hold: 0.12 })
  ],

  dirt: [
    L({ kind: PKIND.DUST, count: 13, life0: 0.9, life1: 1.7, size0: 0.07, size1: 0.6,
        spd0: 0.9, spd1: 2.8, cone: 0.9, grav: 1.1, drag: 1.5,
        cr: 0.46, cg: 0.37, cb: 0.26, alpha: 0.46, turb: 0.09, spin: 1.4, hold: 0.16 }),
    L({ kind: PKIND.CHIP, count: 11, life0: 0.8, life1: 1.4, size0: 0.022, size1: 0.016,
        spd0: 2.2, spd1: 6.4, cone: 0.85, grav: 22, rest: 0.16,
        cr: 0.26, cg: 0.20, cb: 0.13, alpha: 1.0, spin: 13, fadeIn: 0.01, hold: 0.76 })
  ],

  sand: [
    L({ kind: PKIND.DUST, count: 15, life0: 0.9, life1: 1.8, size0: 0.06, size1: 0.62,
        spd0: 1.0, spd1: 3.0, cone: 0.85, grav: 0.9, drag: 1.6,
        cr: 0.76, cg: 0.66, cb: 0.46, alpha: 0.46, turb: 0.1, spin: 1.3, hold: 0.16 }),
    L({ kind: PKIND.CHIP, count: 7, life0: 0.7, life1: 1.2, size0: 0.013, size1: 0.010,
        spd0: 2.0, spd1: 5.4, cone: 0.9, grav: 22, rest: 0.1,
        cr: 0.60, cg: 0.51, cb: 0.34, alpha: 1.0, spin: 10, fadeIn: 0.01, hold: 0.72 })
  ],

  gravel: [
    L({ kind: PKIND.DUST, count: 9, life0: 0.8, life1: 1.5, size0: 0.06, size1: 0.48,
        spd0: 0.9, spd1: 2.6, cone: 0.9, grav: 1.0, drag: 1.7,
        cr: 0.52, cg: 0.48, cb: 0.42, alpha: 0.38, turb: 0.09, hold: 0.16 }),
    L({ kind: PKIND.CHIP, count: 13, life0: 0.9, life1: 1.6, size0: 0.024, size1: 0.019,
        spd0: 2.6, spd1: 7.2, cone: 0.9, grav: 22, rest: 0.32,
        cr: 0.34, cg: 0.32, cb: 0.29, alpha: 1.0, spin: 16, fadeIn: 0.01, hold: 0.78 })
  ],

  grass: [
    L({ kind: PKIND.CHIP, count: 10, life0: 0.8, life1: 1.5, size0: 0.020, size1: 0.016,
        spd0: 2.0, spd1: 5.6, cone: 0.95, grav: 20, rest: 0.12,
        cr: 0.22, cg: 0.31, cb: 0.13, alpha: 1.0, spin: 14, fadeIn: 0.01, hold: 0.74 }),
    L({ kind: PKIND.DUST, count: 8, life0: 0.7, life1: 1.3, size0: 0.05, size1: 0.36,
        spd0: 0.8, spd1: 2.2, cone: 0.95, grav: 1.2, drag: 1.8,
        cr: 0.40, cg: 0.34, cb: 0.22, alpha: 0.32, turb: 0.08, hold: 0.15 })
  ],

  fabric: [
    L({ kind: PKIND.CHIP, count: 9, life0: 0.8, life1: 1.5, size0: 0.016, size1: 0.013,
        spd0: 1.6, spd1: 4.4, cone: 1.0, grav: 15, rest: 0.06, drag: 2.5,
        cr: 0.24, cg: 0.22, cb: 0.20, alpha: 1.0, spin: 12, fadeIn: 0.01, hold: 0.7 }),
    L({ kind: PKIND.DUST, count: 6, life0: 0.6, life1: 1.1, size0: 0.04, size1: 0.28,
        spd0: 0.6, spd1: 1.8, cone: 1.0, grav: 0.2, drag: 2.2,
        cr: 0.48, cg: 0.46, cb: 0.42, alpha: 0.26, turb: 0.08, hold: 0.14 })
  ],

  rubber: [
    L({ kind: PKIND.CHIP, count: 8, life0: 0.7, life1: 1.2, size0: 0.015, size1: 0.012,
        spd0: 2.0, spd1: 5.0, cone: 0.9, grav: 21, rest: 0.4,
        cr: 0.10, cg: 0.10, cb: 0.10, alpha: 1.0, spin: 16, fadeIn: 0.01, hold: 0.76 }),
    L({ kind: PKIND.SMOKE, count: 3, life0: 0.6, life1: 1.2, size0: 0.03, size1: 0.3,
        spd0: 0.5, spd1: 1.5, cone: 1.0, grav: -0.35, drag: 2.2,
        cr: 0.16, cg: 0.15, cb: 0.15, alpha: 0.3, turb: 0.12, hold: 0.1 })
  ],

  sandbag: [
    // jato de areia: cone estreito e rapido saindo do furo
    L({ kind: PKIND.DUST, count: 18, life0: 0.7, life1: 1.5, size0: 0.045, size1: 0.4,
        spd0: 3.2, spd1: 7.5, cone: 0.28, grav: 3.4, drag: 1.3,
        cr: 0.78, cg: 0.68, cb: 0.48, alpha: 0.5, turb: 0.05, spin: 1.2, hold: 0.2 }),
    L({ kind: PKIND.CHIP, count: 9, life0: 0.8, life1: 1.4, size0: 0.011, size1: 0.009,
        spd0: 3.0, spd1: 7.0, cone: 0.35, grav: 22, rest: 0.08,
        cr: 0.64, cg: 0.55, cb: 0.37, alpha: 1.0, spin: 9, fadeIn: 0.01, hold: 0.72 }),
    L({ kind: PKIND.DUST, count: 5, life0: 1.0, life1: 1.9, size0: 0.06, size1: 0.55,
        spd0: 0.5, spd1: 1.6, cone: 1.2, grav: 0.8, drag: 1.7,
        cr: 0.74, cg: 0.65, cb: 0.46, alpha: 0.22, turb: 0.14, hold: 0.12 })
  ],

  water: [
    L({ kind: PKIND.SHARD, count: 14, life0: 0.4, life1: 0.9, size0: 0.02, size1: 0.01,
        spd0: 2.5, spd1: 7.0, cone: 0.7, grav: 20, rest: 0.05,
        cr: 0.80, cg: 0.90, cb: 1.0, alpha: 1.1, fadeIn: 0.01, hold: 0.3 }),
    L({ kind: PKIND.MIST, count: 8, life0: 0.5, life1: 1.0, size0: 0.05, size1: 0.45,
        spd0: 1.0, spd1: 3.0, cone: 0.8, grav: 1.5, drag: 2.2,
        cr: 0.88, cg: 0.92, cb: 0.96, alpha: 0.3, turb: 0.06, hold: 0.14 })
  ],

  flesh: [
    // nevoa curta e discreta: leitura clara de acerto, sem exagero
    L({ kind: PKIND.MIST, count: 11, life0: 0.22, life1: 0.45, size0: 0.05, size1: 0.3,
        spd0: 1.2, spd1: 3.4, cone: 0.95, grav: 3.0, drag: 3.2,
        cr: 0.40, cg: 0.035, cb: 0.03, alpha: 0.5, turb: 0.05, hold: 0.1 }),
    L({ kind: PKIND.CHIP, count: 6, life0: 0.4, life1: 0.8, size0: 0.012, size1: 0.008,
        spd0: 1.8, spd1: 5.0, cone: 0.9, grav: 22, rest: 0.05,
        cr: 0.24, cg: 0.02, cb: 0.02, alpha: 1.0, spin: 8, fadeIn: 0.01, hold: 0.6 })
  ]
};

PROFILES.stone = PROFILES.concrete;

/** Camadas avulsas usadas pelo FxSystem (fumaca do cano, explosao, aterrissagem). */
export const FX_LAYERS = {
  muzzleSmoke: L({
    kind: PKIND.SMOKE, count: 2, life0: 0.55, life1: 1.1, size0: 0.025, size1: 0.22,
    spd0: 0.6, spd1: 1.6, cone: 0.35, up: 0.35, jit: 0.012, off: 0.02,
    grav: -0.5, drag: 2.6, cr: 0.44, cg: 0.42, cb: 0.40,
    alpha: 0.13, turb: 0.09, spin: 1.2, hold: 0.08
  }),
  landDust: L({
    kind: PKIND.DUST, count: 10, life0: 0.6, life1: 1.2, size0: 0.06, size1: 0.5,
    spd0: 1.2, spd1: 3.0, cone: 1.45, jit: 0.12, off: 0.04,
    grav: 0.6, drag: 2.2, cr: 0.62, cg: 0.58, cb: 0.52,
    alpha: 0.3, turb: 0.08, spin: 1.2, hold: 0.12
  }),
  explFire: L({
    kind: PKIND.FIRE, count: 11, life0: 0.26, life1: 0.5, size0: 0.35, size1: 2.1,
    spd0: 1.5, spd1: 5.5, cone: 1.55, jit: 0.25, off: 0.1,
    grav: -1.2, drag: 2.6, cr: 2.6, cg: 1.05, cb: 0.28, colJit: 0.2,
    alpha: 1.0, spin: 2.5, fadeIn: 0.01, hold: 0.06
  }),
  explSmoke: L({
    kind: PKIND.SMOKE, count: 15, life0: 1.6, life1: 3.2, size0: 0.5, size1: 3.4,
    spd0: 1.0, spd1: 4.0, cone: 1.55, jit: 0.35, off: 0.1,
    grav: -0.75, drag: 1.1, cr: 0.15, cg: 0.135, cb: 0.125,
    alpha: 0.55, turb: 0.22, spin: 0.8, hold: 0.12
  }),
  explDust: L({
    kind: PKIND.DUST, count: 20, life0: 1.2, life1: 2.4, size0: 0.28, size1: 2.0,
    spd0: 5.0, spd1: 11.0, cone: 1.57, jit: 0.3, off: 0.05,
    grav: 0.5, drag: 1.5, cr: 0.62, cg: 0.57, cb: 0.5,
    alpha: 0.38, turb: 0.2, spin: 0.9, hold: 0.16
  }),
  explDebris: L({
    kind: PKIND.CHIP, count: 16, life0: 1.0, life1: 2.0, size0: 0.03, size1: 0.022,
    spd0: 6.0, spd1: 17.0, cone: 1.4, jit: 0.2, off: 0.05,
    grav: 22, rest: 0.3, cr: 0.24, cg: 0.23, cb: 0.21,
    alpha: 1.0, spin: 20, fadeIn: 0.01, hold: 0.8
  }),
  explEmber: L({
    kind: PKIND.EMBER, count: 14, life0: 0.7, life1: 1.7, size0: 0.03, size1: 0.008,
    spd0: 3.5, spd1: 12.0, cone: 1.5, jit: 0.15, off: 0.05,
    grav: 13, rest: 0.35, cr: 1.6, cg: 0.55, cb: 0.13,
    alpha: 1.6, fadeIn: 0.01, hold: 0.22
  })
};

// --- temporarios de modulo (zero alocacao no laco de frame) -----------------
const _sunDir = new THREE.Vector3(-0.55, 0.30, 0.78).normalize();
const _sunView = new THREE.Vector3();
const _sunColor = new THREE.Color(1.0, 0.76, 0.52);
const _ambColor = new THREE.Color(0.30, 0.35, 0.44);

// --- ruido usado somente na geracao do atlas (uma vez, no init) -------------
function hash2(x, y) {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return s - Math.floor(s);
}
function vnoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi), b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1), d = hash2(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
function fbm(x, y, oct) {
  let s = 0, amp = 0.5, f = 1;
  for (let i = 0; i < oct; i++) {
    s += vnoise(x * f, y * f) * amp;
    amp *= 0.5;
    f *= 2.05;
  }
  return s;
}

const VERT = /* glsl */`
uniform float uTime;
uniform float uScale;
uniform vec3  uSunDir;
uniform vec3  uSunColor;
uniform vec3  uAmbColor;

attribute vec3 iOrigin;
attribute vec3 iVel;
attribute vec4 iData;   // birth, life, size0, size1
attribute vec4 iPhys;   // grav, drag, rest, groundY
attribute vec4 iRot;    // rot0, spin, kind, seed
attribute vec4 iColor;  // rgb, alpha
attribute vec4 iExtra;  // turb, stretch, fadeIn, hold

varying vec2  vUv;
varying vec4  vColor;
varying vec2  vLocal;

#include <fog_pars_vertex>

void main() {
  float birth = iData.x;
  float life = max(iData.y, 0.001);
  float t = uTime - birth;
  float k = t / life;

  if (t < 0.0 || k >= 1.0) {
    // morta: manda o vertice para fora do volume de recorte, custo de fragmento zero
    vUv = vec2(0.0);
    vColor = vec4(0.0);
    vLocal = vec2(0.0);
    #ifdef USE_FOG
      vFogDepth = 0.0;
    #endif
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float kind = iRot.z;
  float seed = iRot.w;

  float g = iPhys.x;
  float drag = max(iPhys.y, 0.0001);
  float rest = iPhys.z;
  float gy = iPhys.w;

  vec3 p = iOrigin;
  vec3 v = iVel;
  vec3 vNow = v;

  if (rest > 0.0) {
    // Balistica pura com ate 2 quiques num plano horizontal (altura do chao
    // amostrada no momento do impacto). Resolver o instante do quique e uma
    // equacao de segundo grau - barato e da o repique caracteristico da faisca.
    float tr = t;
    for (int i = 0; i < 2; i++) {
      float disc = v.y * v.y + 2.0 * g * (p.y - gy);
      float tHit = 1.0e9;
      if (disc > 0.0 && g > 0.001) {
        float r = (v.y + sqrt(disc)) / g;
        if (r > 1.0e-4) tHit = r;
      }
      if (tHit >= tr) break;
      p.x += v.x * tHit;
      p.z += v.z * tHit;
      p.y = gy;
      float vy = v.y - g * tHit;
      v.y = -vy * rest;
      v.x *= 0.55;
      v.z *= 0.55;
      tr -= tHit;
    }
    p.x += v.x * tr;
    p.z += v.z * tr;
    p.y += v.y * tr - 0.5 * g * tr * tr;
    vNow = vec3(v.x, v.y - g * tr, v.z);
  } else {
    // Arrasto linear resolvido em forma fechada: a poeira freia e depois boia.
    // g negativo = empuxo (poeira quente sobe antes de dispersar).
    vec3 a = vec3(0.0, -g, 0.0);
    vec3 vt = a / drag;
    float e = exp(-drag * t);
    p += vt * t + (v - vt) * (1.0 - e) / drag;
    vNow = vt + (v - vt) * e;
  }

  float turb = iExtra.x;
  if (turb > 0.0) {
    float ph = seed * 43.0;
    p.x += sin(t * 1.7 + ph) * turb * t;
    p.z += cos(t * 1.3 + ph * 1.7) * turb * t;
    p.y += sin(t * 0.9 + ph * 0.5) * turb * 0.4 * t;
  }

  float grow = 1.0 - (1.0 - k) * (1.0 - k);
  float size = mix(iData.z, iData.w, grow) * uScale;

  vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);

  vec2 corner = position.xy;
  vec2 off;
  float stretch = iExtra.y;
  if (stretch > 0.0) {
    // faisca: o quad e esticado na direcao da velocidade projetada na tela,
    // que e o que produz o rastro sem custo de geometria extra
    vec3 vv = (viewMatrix * vec4(vNow, 0.0)).xyz;
    vec2 d = vv.xy;
    float l = length(d);
    vec2 ax = l > 1.0e-4 ? d / l : vec2(0.0, 1.0);
    vec2 ay = vec2(-ax.y, ax.x);
    float sl = 1.0 + min(length(vNow) * stretch, 12.0);
    // corner.y - 0.5 ancora a CABECA quente da faisca na posicao real da
    // particula e joga o rastro para tras; centrado, metade do risco entraria
    // no chao no momento do quique
    off = ax * ((corner.y - 0.5) * size * sl) + ay * (corner.x * size);
  } else {
    float ang = iRot.x + iRot.y * t;
    float ca = cos(ang);
    float sa = sin(ang);
    off = vec2(corner.x * ca - corner.y * sa, corner.x * sa + corner.y * ca) * size;
  }
  mvPosition.xy += off;
  gl_Position = projectionMatrix * mvPosition;

  float a = smoothstep(0.0, max(iExtra.z, 0.002), k) * (1.0 - smoothstep(iExtra.w, 1.0, k));
  a *= iColor.a;

  // estilhaco de vidro cintila ao girar de frente para o sol
  if (abs(kind - 4.0) < 0.5) {
    float tw = pow(abs(sin(t * (22.0 + seed * 46.0) + seed * 9.0)), 4.0);
    a *= 0.22 + 0.78 * tw;
  }

  // varying escrito UMA vez: ler de volta uma saida de vertex shader e legal no
  // papel e problematico em driver antigo
  #ifdef FX_SHADED
    // Dispersao frontal: contra o sol a poeira acende. E o que separa
    // "poeira de verdade" de "mancha cinza".
    vec3 toCam = normalize(p - cameraPosition);
    float fwd = max(dot(toCam, uSunDir), 0.0);
    float scat = pow(fwd, 6.0);
    vec3 lit = uAmbColor + uSunColor * (0.30 + 1.35 * scat);
    vColor = vec4(iColor.rgb * lit, a);
  #else
    vColor = vec4(iColor.rgb, a);
  #endif

  vLocal = corner * 2.0;

  float tile = 0.0;
  if (kind > 1.5 && kind < 2.5) tile = 1.0;
  else if (kind > 2.5 && kind < 3.5) tile = 2.0;
  else if (kind > 3.5 && kind < 4.5) tile = 3.0;
  else if (kind > 5.5 && kind < 6.5) tile = 3.0;
  vec2 tOff = vec2(mod(tile, 2.0), floor(tile * 0.5)) * 0.5;
  vUv = tOff + (uv * 0.94 + 0.03) * 0.5;

  #include <fog_vertex>
}
`;

const FRAG = /* glsl */`
uniform sampler2D uAtlas;
uniform vec3 uSunView;

varying vec2 vUv;
varying vec4 vColor;
varying vec2 vLocal;

// tonemapping_pars e colorspace_pars ja vem no prefixo que o three injeta em
// todo material - incluir de novo aqui daria redefinicao de funcao.
#include <common>
#include <fog_pars_fragment>

void main() {
  vec4 tex = texture2D(uAtlas, vUv);
  float a = tex.a * vColor.a;
  if (a < 0.003) discard;

  vec3 col = vColor.rgb * tex.rgb;

  #ifdef FX_SHADED
    // normal esferica falsa a partir do proprio quad: da volume ao floco de
    // poeira, com o lado voltado ao sol mais claro que o lado de sombra
    float r2 = min(dot(vLocal, vLocal), 1.0);
    vec3 n = vec3(vLocal, sqrt(max(0.0, 1.0 - r2)));
    float ndl = dot(n, uSunView) * 0.5 + 0.5;
    col *= mix(0.55, 1.35, ndl);
  #endif

  gl_FragColor = vec4(col, a);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

export class Particles {
  constructor(ctx) {
    this.ctx = ctx;
    this.pools = null;
    this.texture = null;
    this._time = 0;
    this._countScale = 1;
    this._lightTimer = 99;
  }

  async init() {
    this.texture = this._buildAtlas();

    this.pools = {
      alpha: this._makePool(CAP_ALPHA, false),
      add: this._makePool(CAP_ADD, true)
    };

    const scene = this.ctx.scene;
    scene.add(this.pools.alpha.mesh);
    scene.add(this.pools.add.mesh);
    this._refreshLight();
  }

  // -------------------------------------------------------------------------
  // Construcao
  // -------------------------------------------------------------------------

  _makePool(cap, additive) {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0
    ]), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
      0, 0, 1, 0, 1, 1, 0, 1
    ]), 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.instanceCount = 0;

    const arrays = {
      origin: new Float32Array(cap * 3),
      vel: new Float32Array(cap * 3),
      data: new Float32Array(cap * 4),
      phys: new Float32Array(cap * 4),
      rot: new Float32Array(cap * 4),
      color: new Float32Array(cap * 4),
      extra: new Float32Array(cap * 4)
    };

    const attrs = {};
    const mk = (name, arr, size) => {
      const a = new THREE.InstancedBufferAttribute(arr, size);
      a.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute(name, a);
      attrs[name] = a;
    };
    mk('iOrigin', arrays.origin, 3);
    mk('iVel', arrays.vel, 3);
    mk('iData', arrays.data, 4);
    mk('iPhys', arrays.phys, 4);
    mk('iRot', arrays.rot, 4);
    mk('iColor', arrays.color, 4);
    mk('iExtra', arrays.extra, 4);

    const uniforms = THREE.UniformsUtils.clone(THREE.UniformsLib.fog);
    uniforms.uTime = { value: 0 };
    uniforms.uScale = { value: 1 };
    uniforms.uAtlas = { value: this.texture };
    uniforms.uSunDir = { value: new THREE.Vector3().copy(_sunDir) };
    uniforms.uSunColor = { value: new THREE.Color().copy(_sunColor) };
    uniforms.uAmbColor = { value: new THREE.Color().copy(_ambColor) };
    uniforms.uSunView = { value: new THREE.Vector3(0, 0, 1) };

    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      side: THREE.DoubleSide,
      fog: !additive,
      defines: additive ? {} : { FX_SHADED: '' }
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;      // a posicao real e calculada no shader
    mesh.matrixAutoUpdate = false;   // o shader assume modelMatrix identidade
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = additive ? 12 : 10;
    mesh.layers.enable(LAYERS.FX);
    mesh.raycast = noopRaycast;

    return {
      cap, mesh, geo, mat, arrays, attrs,
      head: 0,
      deathMax: -1,
      dirty: false,
      additive
    };
  }

  /**
   * Atlas 2x2 de 128 px por tile, gerado por canvas 2D:
   *   0 poeira/fumaca (nuvem macia com ruido)
   *   1 lasca solida (silhueta irregular)
   *   2 rastro de faisca (nucleo quente na ponta)
   *   3 brilho em estrela (estilhaco de vidro e brasa)
   */
  _buildAtlas() {
    const S = 256;
    const T = 128;
    const cv = document.createElement('canvas');
    cv.width = S;
    cv.height = S;
    const c2 = cv.getContext('2d');
    const img = c2.createImageData(S, S);
    const d = img.data;

    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const tx = x >= T ? 1 : 0;
        const ty = y >= T ? 1 : 0;
        const tile = ty * 2 + tx;
        const u = (x - tx * T) / T;
        const v = (y - ty * T) / T;
        const cx = (u - 0.5) * 2;
        const cy = (v - 0.5) * 2;
        const r = Math.sqrt(cx * cx + cy * cy);

        let a = 0, lum = 1;

        if (tile === 0) {
          // nuvem macia: queda radial suave modulada por ruido de baixa frequencia
          const n = fbm(u * 3.4 + 11.3, v * 3.4 + 4.7, 4);
          const edge = Math.max(0, 1 - r);
          a = Math.pow(edge, 1.55) * (0.42 + 0.72 * n);
          a *= 1 - Math.pow(Math.min(r, 1), 6);
          lum = 0.82 + 0.28 * n;
        } else if (tile === 1) {
          // lasca: silhueta angular de borda DURA. Com borda macia o detrito
          // vira bolinha de algodao e a poeira e a lasca ficam iguais na tela.
          const ang = Math.atan2(cy, cx);
          const wob = 0.26
            + 0.17 * vnoise(Math.cos(ang) * 1.8 + 7, Math.sin(ang) * 1.8 + 3)
            + 0.08 * Math.abs(Math.cos(ang * 2 + 1.1));
          a = Math.min(1, Math.max(0, (wob - r) / 0.035));
          // facetas chapadas: leitura de fragmento solido virando na luz
          const sect = Math.floor(((ang + Math.PI) / (Math.PI * 2)) * 5);
          lum = 0.42 + 0.55 * hash2(sect * 3.7, 1.3);
        } else if (tile === 2) {
          // rastro: quente e largo na ponta (v alto), fino e apagado na cauda
          const head = v;
          const w = 0.09 + 0.34 * head * head;
          const ax = Math.abs(cx) / w;
          const body = Math.exp(-ax * ax * 2.3);
          a = body * (0.10 + 0.90 * Math.pow(head, 1.6));
          const hot = Math.exp(-(ax * ax * 5.0) - Math.pow((1 - head) * 3.4, 2));
          a = Math.min(1, a + hot * 0.9);
          lum = 0.7 + 0.9 * hot + 0.3 * Math.pow(head, 3);
        } else {
          // estrela: nucleo redondo + 4 pontas finas + 4 diagonais fracas
          const core = Math.exp(-r * r * 26);
          const sx = Math.exp(-(cx * cx) * 340) * Math.exp(-Math.abs(cy) * 4.2);
          const sy = Math.exp(-(cy * cy) * 340) * Math.exp(-Math.abs(cx) * 4.2);
          const d1 = (cx + cy) * 0.7071, d2 = (cx - cy) * 0.7071;
          const sd = (Math.exp(-(d1 * d1) * 700) + Math.exp(-(d2 * d2) * 700)) * Math.exp(-r * 5.0) * 0.45;
          a = Math.min(1, core + sx * 0.8 + sy * 0.8 + sd);
          lum = 0.85 + 0.6 * core;
        }

        a = Math.min(1, Math.max(0, a));
        lum = Math.min(1.6, Math.max(0, lum));
        const c = Math.round(Math.min(255, lum * 255));
        const o = (y * S + x) * 4;
        d[o] = c;
        d[o + 1] = c;
        d[o + 2] = c;
        d[o + 3] = Math.round(a * 255);
      }
    }

    c2.putImageData(img, 0, 0);

    const tex = new THREE.CanvasTexture(cv);
    // flipY FALSE: o shader monta o uv do tile a partir do indice em espaco de
    // pixel do canvas. Com o flip padrao do three, os tiles trocariam de lugar
    // (0 viraria 2) e o rastro da faisca nasceria com a cabeca na cauda.
    tex.flipY = false;
    // sem mipmap: evita sangramento entre tiles do atlas na minificacao
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.colorSpace = THREE.NoColorSpace;
    tex.needsUpdate = true;
    return tex;
  }

  // -------------------------------------------------------------------------
  // Emissao
  // -------------------------------------------------------------------------

  /**
   * Dispara o perfil completo de uma superficie.
   * Todos os parametros sao escalares: nenhum objeto e criado por tiro.
   */
  burst(surface, px, py, pz, nx, ny, nz, groundY, mul) {
    const prof = PROFILES[surface] || PROFILES.concrete;
    for (let i = 0; i < prof.length; i++) {
      this.emit(prof[i], px, py, pz, nx, ny, nz, groundY, mul, 1);
    }
  }

  hasProfile(surface) {
    return !!PROFILES[surface];
  }

  /** Emite uma unica camada. mul escala a quantidade, sizeMul escala o tamanho. */
  emit(layer, px, py, pz, nx, ny, nz, groundY, mul, sizeMul) {
    if (!this.pools) return;
    const pool = ADDITIVE_KINDS[layer.kind] ? this.pools.add : this.pools.alpha;
    const A = pool.arrays;
    const cap = pool.cap;

    const want = layer.count * (mul || 1) * this._countScale;
    let n = Math.round(want);
    if (n <= 0) n = want > 0.34 ? 1 : 0;   // preset baixo nao pode zerar o efeito
    if (n <= 0) return;
    if (n > 160) n = 160;

    // base ortonormal em torno da normal, calculada uma vez por camada
    let tx, ty, tz;
    if (Math.abs(ny) < 0.92) { tx = -nz; ty = 0; tz = nx; } else { tx = 1; ty = 0; tz = 0; }
    let tl = Math.sqrt(tx * tx + ty * ty + tz * tz);
    if (tl < 1e-6) { tx = 1; ty = 0; tz = 0; tl = 1; }
    tx /= tl; ty /= tl; tz /= tl;
    const bx = ny * tz - nz * ty;
    const by = nz * tx - nx * tz;
    const bz = nx * ty - ny * tx;

    const cosCone = Math.cos(layer.cone);
    const sm = sizeMul || 1;
    const now = this._time;

    for (let i = 0; i < n; i++) {
      const idx = pool.head;
      pool.head = (pool.head + 1) % cap;

      const ct = 1 - Math.random() * (1 - cosCone);
      const st = Math.sqrt(Math.max(0, 1 - ct * ct));
      const ph = Math.random() * TAU;
      const cph = Math.cos(ph), sph = Math.sin(ph);

      let dx = nx * ct + (tx * cph + bx * sph) * st;
      let dy = ny * ct + (ty * cph + by * sph) * st;
      let dz = nz * ct + (tz * cph + bz * sph) * st;

      const sp = layer.spd0 + Math.random() * (layer.spd1 - layer.spd0);
      const vx = dx * sp;
      const vy = dy * sp + layer.up;
      const vz = dz * sp;

      const life = layer.life0 + Math.random() * (layer.life1 - layer.life0);
      const sj = 1 + (Math.random() * 2 - 1) * layer.sizeJit;
      const cj = 1 + (Math.random() * 2 - 1) * layer.colJit;

      const o3 = idx * 3;
      const o4 = idx * 4;

      A.origin[o3] = px + nx * layer.off + (Math.random() * 2 - 1) * layer.jit;
      A.origin[o3 + 1] = py + ny * layer.off + (Math.random() * 2 - 1) * layer.jit;
      A.origin[o3 + 2] = pz + nz * layer.off + (Math.random() * 2 - 1) * layer.jit;

      A.vel[o3] = vx;
      A.vel[o3 + 1] = vy;
      A.vel[o3 + 2] = vz;

      A.data[o4] = now;
      A.data[o4 + 1] = life;
      A.data[o4 + 2] = layer.size0 * sj * sm;
      A.data[o4 + 3] = layer.size1 * sj * sm;

      A.phys[o4] = layer.grav;
      A.phys[o4 + 1] = layer.drag;
      A.phys[o4 + 2] = layer.rest;
      A.phys[o4 + 3] = groundY;

      A.rot[o4] = Math.random() * TAU;
      A.rot[o4 + 1] = (Math.random() * 2 - 1) * layer.spin;
      A.rot[o4 + 2] = layer.kind;
      A.rot[o4 + 3] = Math.random();

      A.color[o4] = layer.cr * cj;
      A.color[o4 + 1] = layer.cg * cj;
      A.color[o4 + 2] = layer.cb * cj;
      A.color[o4 + 3] = layer.alpha;

      A.extra[o4] = layer.turb;
      A.extra[o4 + 1] = layer.stretch;
      A.extra[o4 + 2] = layer.fadeIn;
      A.extra[o4 + 3] = layer.hold;

      const death = now + life;
      if (death > pool.deathMax) pool.deathMax = death;
    }

    pool.dirty = true;
    pool.geo.instanceCount = cap;
  }

  // -------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------

  update(dt, time) {
    if (!this.pools) return;

    this._lightTimer += dt;
    if (this._lightTimer > 0.5) {
      this._lightTimer = 0;
      this._refreshLight();
    }

    const cam = this.ctx.camera;
    _sunView.copy(_sunDir);
    if (cam) _sunView.transformDirection(cam.matrixWorldInverse);

    // A qualidade mexe na QUANTIDADE de particulas, quase nao no tamanho:
    // reduzir tamanho descaracteriza o efeito, reduzir quantidade so alivia.
    const ps = Math.min(1.35, Math.max(0.3, this.ctx.settings.particleScale ?? 1));
    this._countScale = ps;
    const scale = 0.86 + 0.14 * ps;

    this._tickPool(this.pools.alpha, time, scale);
    this._tickPool(this.pools.add, time, scale);

    // relogio adiantado em um frame: quem emitir antes do proximo update
    // (armas, balistica, IA) nasce ja no instante do desenho seguinte
    this._time = time + dt;
  }

  _tickPool(pool, time, scale) {
    const u = pool.mat.uniforms;
    u.uTime.value = time;
    u.uScale.value = scale;
    u.uSunDir.value.copy(_sunDir);
    u.uSunColor.value.copy(_sunColor);
    u.uAmbColor.value.copy(_ambColor);
    u.uSunView.value.copy(_sunView);

    if (pool.dirty) {
      // sobe o buffer inteiro apenas nos frames em que algo nasceu.
      // Sem alocar objeto de intervalo por frame (updateRanges aloca).
      const a = pool.attrs;
      a.iOrigin.needsUpdate = true;
      a.iVel.needsUpdate = true;
      a.iData.needsUpdate = true;
      a.iPhys.needsUpdate = true;
      a.iRot.needsUpdate = true;
      a.iColor.needsUpdate = true;
      a.iExtra.needsUpdate = true;
      pool.dirty = false;
    }

    // pool vazio: para de emitir draw call ate a proxima particula
    if (pool.geo.instanceCount !== 0 && time > pool.deathMax) {
      pool.geo.instanceCount = 0;
    }
  }

  /** Le a direcao e a cor do sol do resto do jogo, com queda para valores padrao. */
  _refreshLight() {
    try {
      const sky = this.ctx.sky;
      if (sky && typeof sky.getSunDirection === 'function') {
        const d = sky.getSunDirection();
        if (d && Number.isFinite(d.x) && (d.x !== 0 || d.y !== 0 || d.z !== 0)) {
          _sunDir.set(d.x, d.y, d.z).normalize();
        }
      }
      const sun = this.ctx.lighting?.sun;
      if (sun && sun.color) {
        const inten = Math.min(Math.max(sun.intensity ?? 1, 0.2), 6);
        _sunColor.copy(sun.color).multiplyScalar(0.35 + 0.22 * inten);
      }
      const amb = this.ctx.lighting?.hemi || this.ctx.lighting?.ambient;
      if (amb && amb.color) {
        _ambColor.copy(amb.color).multiplyScalar(Math.min(Math.max(amb.intensity ?? 1, 0.1), 3) * 0.5);
      }
    } catch (err) {
      // iluminacao ainda nao pronta: segue com o padrao de fim de tarde
    }
  }

  dispose() {
    if (!this.pools) return;
    for (const key of ['alpha', 'add']) {
      const pool = this.pools[key];
      pool.mesh.parent?.remove(pool.mesh);
      pool.geo.dispose();
      pool.mat.dispose();
    }
    this.texture?.dispose();
    this.pools = null;
  }
}

// ---------------------------------------------------------------------------
// Ar com particulado (emissor ambiente permanente)
// ---------------------------------------------------------------------------

/**
 * AmbientDust - tira o vacuo do ar.
 *
 * As particulas acima so nascem por impacto, explosao ou cartucho: sem tiro, o
 * ar do mapa e vazio perfeito, e um galpao escuro com o telhado rasgado nao
 * mostra feixe nenhum. Esta classe e o emissor AMBIENTE, ligado por
 * `settings.volumetrics` (a chave existia nos presets e ninguem lia).
 *
 * Como funciona, e por que assim:
 *
 * 1. Um unico InstancedMesh de graos presos numa caixa que acompanha a camera.
 *    Quando um grao passa da parede da caixa, a CPU o traz de volta pela parede
 *    oposta (modulo), entao o campo e infinito com buffer fixo e nada nasce ou
 *    morre. O alpha zera na MESMA medida usada no reposicionamento (distancia de
 *    Chebyshev), logo o salto acontece sempre invisivel.
 *
 * 2. O grao NAO e chapado: ele so acende onde o sol chega de verdade. Para cada
 *    grao ha um teste de linha de visao ate o sol (`collision.lineOfSight`,
 *    zero alocacao, sai no primeiro triangulo), feito em rodizio de poucos por
 *    frame. E isso que produz o eixo de luz: dentro do galpao so a coluna de ar
 *    embaixo do rasgo do telhado fica acesa, o resto some no escuro. Nao e passe
 *    volumetrico nenhum - e a mesma geometria que ja esta na BVH.
 *
 * 3. Dispersao frontal igual a das particulas de impacto: contra o sol o ar
 *    acende, de costas ele desaparece. O termo de ceu tambem dispersa, entao ar
 *    em sombra nao vira preto - fica so muito fraco.
 *
 * Custo por frame: um laco de floats sobre os graos vivos (sem alocar) e no
 * maximo AIR_SAMPLES testes de linha de visao. O resto e um draw call.
 */

const AIR_CAP = 512;              // teto do buffer (preset ultra)
const AIR_BOX = 15.0;             // aresta da caixa que acompanha a camera (m)
const AIR_HALF = AIR_BOX * 0.5;
const AIR_BASE = 380;             // quantidade viva em particleScale = 1
const AIR_MIN = 96;
const AIR_HAZE_EVERY = 7;         // 1 em cada N e vulto grande em vez de grao
const AIR_SAMPLES = 4;            // testes de sombra por frame no regime normal
const AIR_RUSH = 72;              // ... e num mutirao apos salto de camera
const AIR_RUSH_FRAMES = 8;        // AIR_RUSH * AIR_RUSH_FRAMES >= AIR_CAP
const AIR_JUMP = 4.0;             // salto de camera que dispara o mutirao (m)
const AIR_SUN_REACH = 50.0;       // alcance do teste ate o sol (m)

const _camPos = new THREE.Vector3();
const _rayA = new THREE.Vector3();
const _rayB = new THREE.Vector3();
const _bufSize = new THREE.Vector2();

/** Traz um deslocamento de volta para dentro da caixa, em O(1) e sem alocar. */
function airWrap(d) {
  let x = d + AIR_HALF;
  x -= Math.floor(x / AIR_BOX) * AIR_BOX;
  return x - AIR_HALF;
}

const AIR_VERT = /* glsl */`
uniform float uTime;
uniform float uHalf;
uniform float uPixScale;
uniform float uOpacity;
uniform vec3  uSunDir;
uniform vec3  uSunColor;
uniform vec3  uAmbColor;

attribute vec3  aPos;
attribute vec4  aSeed;   // seed, tamanho, tipo (0 grao / 1 vulto), alpha base
attribute float aLit;    // 1 = o sol chega neste ponto, 0 = ponto na sombra

varying vec2 vUv;
varying vec3 vLocal;     // xy = canto do quad, z = tipo
varying vec4 vColor;

void main() {
  float seed = aSeed.x;
  float size = aSeed.y;
  float kind = aSeed.z;
  float base = aSeed.w;
  float ph = seed * 6.2831853;

  // deriva lenta: o ar nao esta parado, mas nada aqui pode "voar"
  vec3 p = aPos;
  p.x += sin(uTime * 0.29 + ph) * 0.20 + sin(uTime * 0.11 + ph * 2.3) * 0.10;
  p.y += sin(uTime * 0.21 + ph * 1.7) * 0.11;
  p.z += cos(uTime * 0.25 + ph * 1.3) * 0.20 + cos(uTime * 0.09 + ph * 3.1) * 0.10;

  vec3 rel = p - cameraPosition;
  float dist = length(rel);

  // Chebyshev sobre aPos (a posicao SEM deriva, que e a que a CPU reposiciona),
  // e nao distancia euclidiana: assim o alpha zera exatamente no eixo e no
  // instante do salto - ele nunca aparece - e a caixa inteira fica aproveitada.
  vec3 anchor = aPos - cameraPosition;
  float cheb = max(max(abs(anchor.x), abs(anchor.y)), abs(anchor.z));

  // O corte de perto cresce com o tamanho: um vulto de 1 m colado na lente
  // cobriria meia tela de overdraw e ainda pareceria borrao. Grao pequeno quase
  // nao sente este termo (fica visivel a partir de ~0.4 m).
  float near0 = 0.35 + size * 2.2;
  float fade = (1.0 - smoothstep(uHalf * 0.68, uHalf * 0.995, cheb))
             * smoothstep(near0, near0 + 0.8 + size * 2.0, dist);

  if (fade < 0.002) {
    vUv = vec2(0.0);
    vLocal = vec3(0.0);
    vColor = vec4(0.0);
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  float d = max(-mv.z, 0.05);

  // um grao de 2 cm a 10 m nao chega a um pixel: cintila e some. Piso em pixel.
  float px = max(size * uPixScale / d, 1.0e-4);
  float s = px < 1.35 ? size * (1.35 / px) : size;

  float ang = ph + uTime * (0.08 + seed * 0.22);
  float ca = cos(ang);
  float sa = sin(ang);
  vec2 c = position.xy;
  mv.xy += vec2(c.x * ca - c.y * sa, c.x * sa + c.y * ca) * s;
  gl_Position = projectionMatrix * mv;

  // Dispersao frontal: olhando contra o sol o ar acende, de costas ele some.
  // O termo de ceu tambem dispersa - por isso ar em sombra nao fica preto.
  // uSunColor ja vem multiplicado pela intensidade do sol (ver _refreshLight),
  // entao os pesos aqui sao baixos de proposito: grao virando estrela e o erro
  // classico de poeira aditiva.
  vec3 toCam = rel / max(dist, 0.001);
  float scat = pow(max(dot(toCam, uSunDir), 0.0), 5.0);
  vec3 lum = uAmbColor * (0.5 + 1.4 * scat) + uSunColor * aLit * (0.18 + 1.35 * scat);

  vUv = (uv * 0.94 + 0.03) * 0.5;   // tile 0 do atlas: nuvem macia
  vLocal = vec3(c * 2.0, kind);
  vColor = vec4(lum, base * fade * uOpacity);
}
`;

const AIR_FRAG = /* glsl */`
uniform sampler2D uAtlas;

varying vec2 vUv;
varying vec3 vLocal;
varying vec4 vColor;

#include <common>

void main() {
  // grao: mancha redonda calculada no proprio quad, sem textura - com 2 pixels
  // de tamanho, amostrar o atlas so daria aliasing. Vulto: nuvem do atlas.
  float grain = exp(-dot(vLocal.xy, vLocal.xy) * 3.4);
  float haze = texture2D(uAtlas, vUv).a;
  float a = mix(grain, haze, vLocal.z) * vColor.a;
  if (a < 0.0025) discard;
  gl_FragColor = vec4(vColor.rgb, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class AmbientDust {
  constructor(ctx, atlas) {
    this.ctx = ctx;
    this.mesh = null;
    this._atlas = atlas || null;
    this._geo = null;
    this._mat = null;
    this._attrPos = null;
    this._attrLit = null;

    this._pos = new Float32Array(AIR_CAP * 3);
    this._seed = new Float32Array(AIR_CAP * 4);
    this._lit = new Float32Array(AIR_CAP);
    this._goal = new Float32Array(AIR_CAP);
    this._seen = new Uint8Array(AIR_CAP);

    // fila de prioridade dos graos que mudaram de lugar e precisam de novo teste
    this._queue = new Int32Array(AIR_CAP + 1);
    this._marked = new Uint8Array(AIR_CAP);
    this._qHead = 0;
    this._qTail = 0;

    this._live = 0;
    this._on = false;
    this._scale = -1;
    this._cursor = 0;
    this._rush = 0;
    this._placed = false;
    this._camX = 0; this._camY = 0; this._camZ = 0;
    this._noSun = false;
    this._warned = false;
  }

  init() {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0
    ]), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
      0, 0, 1, 0, 1, 1, 0, 1
    ]), 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.instanceCount = 0;

    // tipo e tamanho sao sorteados UMA vez; o indice decide quem e vulto, para
    // a proporcao continuar a mesma com qualquer quantidade viva
    for (let i = 0; i < AIR_CAP; i++) {
      const o4 = i * 4;
      const haze = (i % AIR_HAZE_EVERY) === 0;
      this._seed[o4] = Math.random();
      this._seed[o4 + 1] = haze ? 0.42 + Math.random() * 0.72 : 0.012 + Math.random() * 0.022;
      this._seed[o4 + 2] = haze ? 1 : 0;
      this._seed[o4 + 3] = haze ? 0.055 : 0.18;
    }

    const aPos = new THREE.InstancedBufferAttribute(this._pos, 3);
    const aSeed = new THREE.InstancedBufferAttribute(this._seed, 4);
    const aLit = new THREE.InstancedBufferAttribute(this._lit, 1);
    aPos.setUsage(THREE.DynamicDrawUsage);
    aLit.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aPos', aPos);
    geo.setAttribute('aSeed', aSeed);
    geo.setAttribute('aLit', aLit);

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uHalf: { value: AIR_HALF },
        uPixScale: { value: 700 },
        uOpacity: { value: 1 },
        uAtlas: { value: this._atlas },
        uSunDir: { value: new THREE.Vector3().copy(_sunDir) },
        uSunColor: { value: new THREE.Color().copy(_sunColor) },
        uAmbColor: { value: new THREE.Color().copy(_ambColor) }
      },
      vertexShader: AIR_VERT,
      fragmentShader: AIR_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;      // a posicao real e calculada no shader
    mesh.matrixAutoUpdate = false;   // o shader assume modelMatrix identidade
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = 9;            // antes das particulas de impacto
    mesh.visible = false;
    mesh.layers.enable(LAYERS.FX);
    mesh.raycast = noopRaycast;
    this.ctx.scene.add(mesh);

    this.mesh = mesh;
    this._geo = geo;
    this._mat = mat;
    this._attrPos = aPos;
    this._attrLit = aLit;
  }

  update(dt, time) {
    if (!this.mesh) return;

    const st = this.ctx.settings;
    const on = !!(st && st.volumetrics);
    const ps = Math.min(1.4, Math.max(0.3, (st && st.particleScale) || 1));
    if (on !== this._on || ps !== this._scale) this._configure(on, ps);

    const live = this._live;
    if (live <= 0) return;

    const cam = this.ctx.camera;
    if (!cam) return;
    cam.getWorldPosition(_camPos);
    const cx = _camPos.x, cy = _camPos.y, cz = _camPos.z;

    // Salto de camera (respawn, viewpoint de depuracao): reposiciona o campo e
    // pede mutirao de testes. Sem isso o ar apareceria com a luz do lugar antigo.
    const jumped = !this._placed
      || Math.abs(cx - this._camX) > AIR_JUMP
      || Math.abs(cy - this._camY) > AIR_JUMP
      || Math.abs(cz - this._camZ) > AIR_JUMP;
    this._camX = cx; this._camY = cy; this._camZ = cz;
    if (jumped) this._scatter(0, live, cx, cy, cz);

    const pos = this._pos;
    const lit = this._lit;
    const goal = this._goal;
    let posDirty = jumped;
    let litDirty = jumped;
    const k = Math.min(1, dt * 6);

    for (let i = 0; i < live; i++) {
      const o = i * 3;
      let moved = false;

      let d = pos[o] - cx;
      if (d > AIR_HALF || d < -AIR_HALF) { pos[o] = cx + airWrap(d); moved = true; }
      d = pos[o + 1] - cy;
      if (d > AIR_HALF || d < -AIR_HALF) { pos[o + 1] = cy + airWrap(d); moved = true; }
      d = pos[o + 2] - cz;
      if (d > AIR_HALF || d < -AIR_HALF) { pos[o + 2] = cz + airWrap(d); moved = true; }
      if (moved) { posDirty = true; this._mark(i); }

      // o grao guarda a luz antiga ate o teste novo chegar; como ele so se
      // reposiciona na borda (onde o alpha e zero), a troca nunca aparece
      const l = lit[i];
      const g = goal[i];
      if (l !== g) {
        const nl = l + (g - l) * k;
        lit[i] = Math.abs(g - nl) < 0.003 ? g : nl;
        litDirty = true;
      }
    }

    const u = this._mat.uniforms;
    u.uTime.value = time;
    u.uSunDir.value.copy(_sunDir);
    u.uSunColor.value.copy(_sunColor);
    u.uAmbColor.value.copy(_ambColor);
    u.uPixScale.value = this._pixelScale(cam);

    if (this._sampleSun(live, pos, lit, goal)) litDirty = true;

    if (posDirty) this._attrPos.needsUpdate = true;
    if (litDirty) this._attrLit.needsUpdate = true;
  }

  // -------------------------------------------------------------------------
  // Apoio
  // -------------------------------------------------------------------------

  /** Quantos pixels de altura tem 1 metro a 1 metro da camera. */
  _pixelScale(cam) {
    let h = 1080;
    const r = this.ctx.renderer;
    try {
      if (r && typeof r.getDrawingBufferSize === 'function') {
        r.getDrawingBufferSize(_bufSize);
        if (_bufSize.y > 1) h = _bufSize.y;
      }
    } catch (err) {
      this._warn(err);
    }
    const e = cam.projectionMatrix && cam.projectionMatrix.elements;
    const p11 = e && Number.isFinite(e[5]) && e[5] > 0.01 ? e[5] : 1.2;
    return 0.5 * h * p11;
  }

  /**
   * Rodizio dos testes de sombra. Poucos por frame no regime normal; mutirao
   * logo depois de um salto de camera, para o ar ja estar certo em ~8 frames
   * (a revisao visual tira o print 14 frames depois do teleporte).
   */
  _sampleSun(live, pos, lit, goal) {
    if (this._noSun) return false;
    const col = this.ctx.collision;
    if (!col || typeof col.lineOfSight !== 'function') return false;

    let budget = AIR_SAMPLES;
    if (this._rush > 0) { this._rush--; budget = AIR_RUSH; }

    let snapped = false;
    for (let s = 0; s < budget; s++) {
      const i = this._next(live);
      if (i < 0) break;
      const o = i * 3;
      _rayA.set(pos[o], pos[o + 1], pos[o + 2]);
      _rayB.copy(_rayA).addScaledVector(_sunDir, AIR_SUN_REACH);

      let clear = true;
      try {
        clear = col.lineOfSight(_rayA, _rayB) !== false;
      } catch (err) {
        // colisao indisponivel: o ar segue com a luz que tem, sem derrubar nada
        this._noSun = true;
        this._warn(err);
        break;
      }

      const g = clear ? 1 : 0;
      goal[i] = g;
      if (!this._seen[i]) {
        // primeiro teste do grao: entra ja com o valor certo, sem acender aos
        // poucos (a suavizacao so vale para mudanca de luz, nao para estreia)
        this._seen[i] = 1;
        lit[i] = g;
        snapped = true;
      }
    }
    return snapped;
  }

  /** Proximo grao a testar: primeiro os que mudaram de lugar, depois rodizio. */
  _next(live) {
    while (this._qHead !== this._qTail) {
      const i = this._queue[this._qHead];
      this._qHead = (this._qHead + 1) % (AIR_CAP + 1);
      this._marked[i] = 0;
      if (i < live) return i;
    }
    if (live <= 0) return -1;
    const i = this._cursor % live;
    this._cursor = (i + 1) % live;
    return i;
  }

  _mark(i) {
    if (this._marked[i]) return;
    const next = (this._qTail + 1) % (AIR_CAP + 1);
    if (next === this._qHead) return;   // fila cheia: o rodizio pega depois
    this._marked[i] = 1;
    this._queue[this._qTail] = i;
    this._qTail = next;
  }

  _scatter(from, to, cx, cy, cz) {
    const pos = this._pos;
    for (let i = from; i < to; i++) {
      const o = i * 3;
      pos[o] = cx + (Math.random() - 0.5) * AIR_BOX;
      pos[o + 1] = cy + (Math.random() - 0.5) * AIR_BOX;
      pos[o + 2] = cz + (Math.random() - 0.5) * AIR_BOX;
      // nasce apagado: errar para menos ar e melhor que acender poeira dentro
      // de um galpao escuro so porque o teste ainda nao rodou
      this._seen[i] = 0;
      this._lit[i] = 0;
      this._goal[i] = 0;
    }
    this._placed = true;
    this._rush = AIR_RUSH_FRAMES;
    this._qHead = 0;
    this._qTail = 0;
    this._marked.fill(0);
    this._cursor = 0;
  }

  _configure(on, ps) {
    this._on = on;
    this._scale = ps;
    const want = on ? Math.min(AIR_CAP, Math.max(AIR_MIN, Math.round(AIR_BASE * ps))) : 0;
    const prev = this._live;
    this._live = want;
    this._geo.instanceCount = want;
    this.mesh.visible = want > 0;

    if (want === 0) {
      this._placed = false;   // ao religar, o campo e refeito em volta da camera
      return;
    }
    if (want > prev && this._placed) {
      const cam = this.ctx.camera;
      if (!cam) { this._placed = false; return; }
      cam.getWorldPosition(_camPos);
      this._scatter(prev, want, _camPos.x, _camPos.y, _camPos.z);
    }
  }

  _warn(err) {
    if (this._warned) return;
    this._warned = true;
    console.warn('[fx] ar com particulado degradado:', err);
  }

  dispose() {
    if (!this.mesh) return;
    this.mesh.parent?.remove(this.mesh);
    this._geo.dispose();
    this._mat.dispose();
    this.mesh = null;
    this._attrPos = null;
    this._attrLit = null;
  }
}

/** InstancedMesh de FX nunca deve aparecer em raycast de gameplay. */
function noopRaycast() { /* intencionalmente vazio */ }
